/**
 * Campaign Types - TypeScript Definitions for Campaign Management
 */

// Campaign Status enum
export enum CampaignStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ARCHIVED = 'ARCHIVED',
  EXPIRED = 'EXPIRED',
  SCHEDULED = 'SCHEDULED',
  INACTIVE = 'INACTIVE', // Keep for backward compatibility
}

// Campaign Type enum
export enum CampaignType {
  FESTIVAL = 'FESTIVAL',
  SEASONAL = 'SEASONAL', 
  PERFORMANCE = 'PERFORMANCE',
  PARTNER = 'PARTNER',
  INTERNAL = 'INTERNAL'
}

// Funding type
export enum FundingType {
  PLATFORM = 'PLATFORM',
  SHARED = 'SHARED'
}

// Base Campaign interface
export interface Campaign {
  id: string;
  name: string;
  description?: string;
  campaignType: CampaignType;
  status: CampaignStatus;
  startAt: string; // ISO date string
  endAt: string; // ISO date string
  createdAt: string;
  updatedAt: string;
  
  // Metrics (from listing API)
  activePromos?: number;
  totalUsage?: number;
  revenueImpact?: number; // In currency
  revenueUplift?: string; // Percentage like "+12.4%"
  
  // Funding information
  funding?: {
    fundedBy: FundingType;
    platformShare?: number; // Percentage (0-100)
  };
  
  // Linked promos
  linkedPromos?: string[]; // Array of promo IDs
  linkedPromoDetails?: LinkedPromo[]; // Populated promo details
}

// Linked promo summary for campaigns
export interface LinkedPromo {
  id: string;
  name: string;
  code: string;
  domain: string;
  discount: string; // e.g., "20% OFF", "₹100 OFF"
  status: string;
}

// Campaign form data for creation/editing
export interface CampaignFormData {
  name: string;
  description?: string;
  campaignType: CampaignType;
  startAt: string;
  endAt: string;
  linkedPromos?: string[];
  funding?: {
    fundedBy: FundingType;
    platformShare?: number;
  };
}

// Campaign creation request payload
export interface CreateCampaignRequest {
  name: string;
  description?: string;
  campaignType: CampaignType;
  startAt: string; // ISO date string
  endAt: string; // ISO date string
  linkedPromos?: string[];
  funding?: {
    fundedBy: FundingType;
    platformShare?: number;
  };
}

// Campaign list API response
export interface CampaignListResponse {
  campaigns: Campaign[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

// Campaign list API request params
export interface CampaignListParams {
  status?: CampaignStatus;
  campaignType?: CampaignType;
  pageNum?: number;
  pageSize?: number;
  search?: string;
  sortBy?: 'name' | 'createdAt' | 'startAt' | 'endAt';
  sortOrder?: 'asc' | 'desc';
  startAt?: string; // Date string in YYYY-MM-DD format
  endAt?: string; // Date string in YYYY-MM-DD format
}

// Campaign validation errors
export interface CampaignValidationErrors {
  name?: string;
  description?: string;
  campaignType?: string;
  startAt?: string;
  endAt?: string;
  funding?: {
    fundedBy?: string;
    platformShare?: string;
  };
}

// Stepper step configuration
export interface CampaignStepConfig {
  id: string;
  title: string;
  isCompleted: boolean;
  isActive: boolean;
  isOptional?: boolean;
}

// Campaign stepper state
export interface CampaignStepperState {
  currentStep: number;
  steps: CampaignStepConfig[];
  formData: CampaignFormData;
  validationErrors: CampaignValidationErrors;
  isDraft: boolean;
  isSubmitting: boolean;
}
